/**
 * 6x6 Sudoku Generator - Client-side version for offline play
 * 6x6 Sudoku has:
 * - 6 rows
 * - 6 columns
 * - 6 boxes (2 rows x 3 columns each)
 */

const BOARD_SIZE = 6;
const BOX_ROWS = 2;
const BOX_COLS = 3;

/**
 * Check if a value can be placed at the given position
 */
function isValidPlacement(board, row, col, value) {
  // Check row
  for (let c = 0; c < BOARD_SIZE; c++) {
    if (board[row][c] === value) return false;
  }

  // Check column
  for (let r = 0; r < BOARD_SIZE; r++) {
    if (board[r][col] === value) return false;
  }

  // Check box (2x3 boxes)
  const boxRow = Math.floor(row / BOX_ROWS) * BOX_ROWS;
  const boxCol = Math.floor(col / BOX_COLS) * BOX_COLS;

  for (let r = boxRow; r < boxRow + BOX_ROWS; r++) {
    for (let c = boxCol; c < boxCol + BOX_COLS; c++) {
      if (board[r][c] === value) return false;
    }
  }

  return true;
}

/**
 * Find an empty cell in the board
 */
function findEmptyCell(board) {
  for (let row = 0; row < BOARD_SIZE; row++) {
    for (let col = 0; col < BOARD_SIZE; col++) {
      if (board[row][col] === 0) return [row, col];
    }
  }
  return null;
}

/**
 * Shuffle an array
 */
function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/**
 * Fill remaining cells using backtracking
 */
function fillBoard(board) {
  const emptyCell = findEmptyCell(board);
  if (!emptyCell) return true; // Board is full

  const [row, col] = emptyCell;
  const numbers = shuffleArray([1, 2, 3, 4, 5, 6]);

  for (const num of numbers) {
    if (isValidPlacement(board, row, col, num)) {
      board[row][col] = num;
      if (fillBoard(board)) return true;
      board[row][col] = 0;
    }
  }

  return false;
}

/**
 * Solve the puzzle using backtracking
 */
function solvePuzzle(board) {
  const solution = board.map(row => [...row]);
  if (solveHelper(solution)) return solution;
  return null;
}

function solveHelper(board) {
  const emptyCell = findEmptyCell(board);
  if (!emptyCell) return true;

  const [row, col] = emptyCell;
  const numbers = [1, 2, 3, 4, 5, 6];

  for (const num of numbers) {
    if (isValidPlacement(board, row, col, num)) {
      board[row][col] = num;
      if (solveHelper(board)) return true;
      board[row][col] = 0;
    }
  }

  return false;
}

/**
 * Remove numbers from the board to create a puzzle
 */
function removeNumbers(board, difficulty) {
  const cellsToRemove = difficulty === 'easy' ? 12 : 
                       difficulty === 'medium' ? 18 : 24;
  
  const newBoard = board.map(row => [...row]);
  let removed = 0;
  
  while (removed < cellsToRemove) {
    const row = Math.floor(Math.random() * BOARD_SIZE);
    const col = Math.floor(Math.random() * BOARD_SIZE);
    
    if (newBoard[row][col] !== 0) {
      const tempValue = newBoard[row][col];
      newBoard[row][col] = 0;
      
      // Check if puzzle still has unique solution
      const solution = solvePuzzle(newBoard);
      if (solution) {
        removed++;
      } else {
        newBoard[row][col] = tempValue;
      }
    }
  }
  
  return newBoard;
}

/**
 * Generate a new Sudoku puzzle (fully offline)
 */
function generate(difficulty = 'easy') {
  // Create empty board
  const board = Array(BOARD_SIZE).fill(0).map(() => Array(BOARD_SIZE).fill(0));
  
  // Fill the board
  fillBoard(board);
  
  // Store the solution
  const solution = board.map(row => [...row]);
  
  // Create initial board with removed numbers
  const initialBoard = removeNumbers(board, difficulty);
  
  return {
    board: initialBoard,
    solution,
    initialBoard
  };
}

/**
 * Validate a move
 */
function isValidMove(board, row, col, value) {
  if (board[row][col] !== 0) return false;
  return isValidPlacement(board, row, col, value);
}

/**
 * Get a hint for a specific position
 */
function getHint(board, row, col, solution) {
  if (row !== undefined && col !== undefined) {
    if (board[row][col] !== 0) {
      return { message: 'This cell is already filled!', row, col };
    }
    return {
      message: `The answer is ${solution[row][col]}`,
      row,
      col,
      value: solution[row][col]
    };
  }
  
  for (let r = 0; r < BOARD_SIZE; r++) {
    for (let c = 0; c < BOARD_SIZE; c++) {
      if (board[r][c] === 0) {
        return {
          message: `Hint: The answer is ${solution[r][c]}`,
          row: r,
          col: c,
          value: solution[r][c]
        };
      }
    }
  }
  
  return { message: 'No hints needed!' };
}

/**
 * Check if the puzzle is solved
 */
function isPuzzleSolved(board, solution) {
  for (let row = 0; row < BOARD_SIZE; row++) {
    for (let col = 0; col < BOARD_SIZE; col++) {
      if (board[row][col] !== solution[row][col]) {
        return false;
      }
    }
  }
  return true;
}

/**
 * Count filled cells
 */
function countFilledCells(board) {
  let count = 0;
  for (let row = 0; row < BOARD_SIZE; row++) {
    for (let col = 0; col < BOARD_SIZE; col++) {
      if (board[row][col] !== 0) count++;
    }
  }
  return count;
}

export {
  generate,
  isValidMove,
  getHint,
  isPuzzleSolved,
  countFilledCells,
  isValidPlacement,
  BOARD_SIZE,
  BOX_ROWS,
  BOX_COLS
};